// Client for the /api/games/* endpoints. All calls are best-effort — if the user is
// anonymous (no session token), or the network fails, we return null instead of throwing.
// The game itself runs in the engine locally; recording is purely additive.

import { loadToken } from "../auth/storage";
import type { Capture, Move } from "../engine";

function apiBase(): string {
  const url = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? "";
  return url.replace(/\/+$/, "");
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response | null> {
  const token = loadToken();
  if (!token) return null;
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  headers.set("authorization", `Bearer ${token}`);
  try {
    return await fetch(`${apiBase()}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
  } catch {
    return null;
  }
}

export interface CreateGameInput {
  mode: "bot" | "online";
  victoryLevel: string;
  threshold?: number | null;
  humanSide?: "white" | "black";
}

export interface AppendMoveInput {
  turnNumber: number;
  side: "white" | "black";
  move: Move;
  declared?: Capture | null;
  advance?: boolean;
}

export interface EndGameInput {
  winnerSide: "white" | "black";
  victoryKind: string;
  victoryDetail?: string;
  whiteFinalScore: number;
  blackFinalScore: number;
}

export interface GameSummary {
  id: string;
  mode: "bot" | "online";
  whiteUserId: string | null;
  blackUserId: string | null;
  victoryLevel: string;
  threshold: number | null;
  startedAt: string;
  endedAt: string | null;
  winnerSide: "white" | "black" | null;
  victoryKind: string | null;
  victoryDetail: string | null;
  whiteFinalScore: number | null;
  blackFinalScore: number | null;
}

export interface GameMoveRecord {
  turnNumber: number;
  side: "white" | "black";
  move: Move;
  declared: Capture | null;
  advance: boolean;
  playedAt: string;
}

export async function createGameRecord(input: CreateGameInput): Promise<{ gameId: string } | null> {
  const res = await authedFetch("/api/games", { method: "POST", body: JSON.stringify(input) });
  if (!res || !res.ok) return null;
  try {
    return (await res.json()) as { gameId: string };
  } catch {
    return null;
  }
}

export async function appendMoveRecord(gameId: string, input: AppendMoveInput): Promise<boolean> {
  const res = await authedFetch(`/api/games/${gameId}/moves`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return !!res && res.ok;
}

export async function endGameRecord(gameId: string, input: EndGameInput): Promise<boolean> {
  const res = await authedFetch(`/api/games/${gameId}/end`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return !!res && res.ok;
}

export async function listGames(params: { cursor?: string; limit?: number } = {}): Promise<{ games: GameSummary[]; nextCursor: string | null } | null> {
  const url = new URL(`${apiBase()}/api/games`, window.location.origin);
  if (params.cursor) url.searchParams.set("cursor", params.cursor);
  if (params.limit) url.searchParams.set("limit", String(params.limit));
  const path = url.pathname + (url.search || "");
  const res = await authedFetch(path, { method: "GET" });
  if (!res || !res.ok) return null;
  try {
    return (await res.json()) as { games: GameSummary[]; nextCursor: string | null };
  } catch {
    return null;
  }
}

export async function getGameWithMoves(gameId: string): Promise<{ game: GameSummary; moves: GameMoveRecord[] } | null> {
  const res = await authedFetch(`/api/games/${gameId}`, { method: "GET" });
  if (!res || !res.ok) return null;
  try {
    return (await res.json()) as { game: GameSummary; moves: GameMoveRecord[] };
  } catch {
    return null;
  }
}
